import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Sparkles, Clock, CheckCircle2, AlertCircle, X, Grid3x3, List, MoreVertical, Trash2, Copy, ExternalLink, Users, Phone, Headphones, Command, Search, Filter, SortAsc, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { ThemeToggle } from "../components/ui/ThemeToggle";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ProgressRing } from "../components/ui/progress-ring";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../hooks/useAuth";
import { trpc } from "../lib/trpc";
import LoadingSpinner from "../components/LoadingSpinner";
import { toast } from "sonner";
import { CommandPalette } from "../components/CommandPalette";

/**
 * Project Card Component with premium animations
 */
interface ProjectCardProps {
  // Loose typing against the tRPC projects.list response — a precise shape
  // is a Phase 9 follow-up. `unknown` would be safer but costs a refactor
  // of every property access in this file.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  project: any;
  index: number;
  viewMode: "grid" | "list";
  onNavigate: () => void;
  activeMenuId: string | null;
  setActiveMenuId: (id: string | null) => void;
  onDelete: (projectId: string, projectName: string) => void;
}

function ProjectCard({
  project,
  index,
  viewMode,
  onNavigate,
  activeMenuId,
  setActiveMenuId,
  onDelete,
}: ProjectCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Map step to normalized index (0-10 for 11 logical steps)
  // Note: DB stores with underscores, frontend uses hyphens - normalize to handle both
  const getStepIndex = (step: string): number => {
    const normalizedStep = step.replace(/_/g, "-");
    const stepMap: Record<string, number> = {
      "company-info": 0,
      "sms-compliance": 1,
      "extensions": 2,
      "timeframes": 3,
      "tod-routers": 4,
      "ring-groups": 5,
      "queues": 6,
      "auto-attendants": 7,
      "phone-numbers": 8,
      "phones": 9,
      "review": 10,
    };
    return stepMap[normalizedStep] ?? -1;
  };

  // Calculate progress based on status and current step
  const calculateProgress = (status: string, currentStep?: string): number => {
    const TOTAL_STEPS = 11; // Total wizard steps

    switch (status) {
      case "provisioned":
        return 100;
      case "approved":
        return 95;
      case "in_review":
        return 90;
      case "draft":
      default: {
        // For draft, calculate based on wizard step (5-85% range)
        if (!currentStep) return 5; // Just started
        const stepIndex = getStepIndex(currentStep);
        if (stepIndex === -1) return 5;
        // Progress from 5% to 85% based on step (leaving room for review/approval/provisioning)
        return Math.round(5 + (stepIndex / (TOTAL_STEPS - 1)) * 80);
      }
    }
  };

  const getStatusConfig = (status: string, currentStep?: string) => {
    const progress = calculateProgress(status, currentStep);

    const configs = {
      draft: {
        label: "Draft",
        icon: Clock,
        variant: "warning" as const,
      },
      in_review: {
        label: "In Review",
        icon: AlertCircle,
        variant: "primary" as const,
      },
      approved: {
        label: "Approved",
        icon: CheckCircle2,
        variant: "success" as const,
      },
      provisioned: {
        label: "Provisioned",
        icon: Sparkles,
        variant: "success" as const,
      },
    };
    const config = configs[status as keyof typeof configs] || configs.draft;
    return { ...config, progress };
  };

  const getStatusBadge = (status: string, currentStep?: string) => {
    const config = getStatusConfig(status, currentStep);
    const Icon = config.icon;

    const variantClasses = {
      warning: "bg-brand-warning/10 text-brand-warning border-brand-warning/20",
      primary: "bg-brand-primary/10 text-brand-primary border-brand-primary/20",
      success: "bg-brand-success/10 text-brand-success border-brand-success/20",
      danger: "bg-brand-danger/10 text-brand-danger border-brand-danger/20",
    };

    return (
      <motion.span
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border-2 ${variantClasses[config.variant]}`}
      >
        <Icon className="w-4 h-4" />
        {config.label}
      </motion.span>
    );
  };

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(activeMenuId === project.id ? null : project.id);
  };

  const statusConfig = getStatusConfig(project.status, project.currentStep);

  // Stats from project configuration (set by admin when creating project)
  const stats = {
    seats: project.seats || 0,
    dids: project.didsCount || 0,
    agents: project.callCenterAgents || 0,
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative"
    >
      <Card
        variant="glass"
        className={`cursor-pointer group ${
          viewMode === "list" ? "flex items-center" : ""
        }`}
        onClick={onNavigate}
      >
        <CardHeader className={viewMode === "list" ? "flex-1" : ""}>
          <div className="flex items-start justify-between gap-4">
            {/* Left: Title & Status */}
            <div className="flex-1 min-w-0">
              <CardTitle className="text-xl mb-2 truncate group-hover:text-brand-primary transition-colors">
                {project.name}
              </CardTitle>
              {getStatusBadge(project.status, project.currentStep)}
            </div>

            {/* Right: Progress Ring & Menu */}
            <div className="flex items-center gap-3">
              {viewMode === "grid" && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: index * 0.05 + 0.2 }}
                >
                  <ProgressRing
                    progress={statusConfig.progress}
                    size={64}
                    strokeWidth={5}
                    variant={statusConfig.variant}
                    showLabel={true}
                  />
                </motion.div>
              )}

              {/* Quick actions menu */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMenu}
                  className={`transition-opacity ${isHovered ? "opacity-100" : "opacity-0"}`}
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>

                {/* Dropdown menu */}
                <AnimatePresence>
                  {activeMenuId === project.id && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-10 z-50 w-48 glass-card py-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="w-full px-4 py-2 text-left text-sm hover:bg-background-hover flex items-center gap-2 transition-colors"
                        onClick={() => {
                          onNavigate();
                          setActiveMenuId(null);
                        }}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open
                      </button>
                      <button
                        className="w-full px-4 py-2 text-left text-sm hover:bg-background-hover flex items-center gap-2 transition-colors"
                        onClick={() => {
                          navigator.clipboard.writeText(project.id);
                          toast.success("Project ID copied!");
                          setActiveMenuId(null);
                        }}
                      >
                        <Copy className="w-4 h-4" />
                        Copy ID
                      </button>
                      <div className="border-t border-border my-1" />
                      <button
                        className="w-full px-4 py-2 text-left text-sm hover:bg-brand-danger/10 text-brand-danger flex items-center gap-2 transition-colors"
                        onClick={() => {
                          onDelete(project.id, project.name);
                          setActiveMenuId(null);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </CardHeader>

        {viewMode === "grid" && (
          <CardContent className="space-y-4">
            {/* Stats Grid - Only show stats that have values */}
            <div className="grid grid-cols-3 gap-3">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 + 0.3 }}
                className="flex flex-col items-center p-3 rounded-lg bg-background-hover/50 border border-border/50 hover:border-brand-primary/50 transition-colors"
              >
                <Users className="w-5 h-5 text-brand-primary mb-1" />
                <span className="text-xl font-bold text-foreground">{stats.seats}</span>
                <span className="text-xs text-muted-foreground">Seats</span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 + 0.35 }}
                className="flex flex-col items-center p-3 rounded-lg bg-background-hover/50 border border-border/50 hover:border-brand-accent/50 transition-colors"
              >
                <Phone className="w-5 h-5 text-brand-accent mb-1" />
                <span className="text-xl font-bold text-foreground">{stats.dids}</span>
                <span className="text-xs text-muted-foreground">DIDs</span>
              </motion.div>

              {stats.agents > 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.4 }}
                  className="flex flex-col items-center p-3 rounded-lg bg-background-hover/50 border border-border/50 hover:border-brand-success/50 transition-colors"
                >
                  <Headphones className="w-5 h-5 text-brand-success mb-1" />
                  <span className="text-xl font-bold text-foreground">{stats.agents}</span>
                  <span className="text-xs text-muted-foreground">CC Agents</span>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.4 }}
                  className="flex flex-col items-center p-3 rounded-lg bg-background-hover/50 border border-border/50"
                >
                  <span className="text-xs text-muted-foreground text-center">
                    {project.projectType === "mac_order" ? "MAC Order" :
                     project.projectType === "new_location" ? "New Location" : "New Customer"}
                  </span>
                </motion.div>
              )}
            </div>

            {/* Meta Info */}
            <div className="flex items-center justify-between text-sm pt-3 border-t border-border/50">
              <span className="text-muted-foreground">
                Updated {new Date(project.updatedAt).toLocaleDateString()}
              </span>
              <span className="text-xs text-muted-foreground capitalize">
                {project.currentStep?.replace("_", " ") || "Not started"}
              </span>
            </div>
          </CardContent>
        )}

        {viewMode === "list" && (
          <CardContent className="flex items-center gap-8 py-4">
            <div className="flex items-center gap-2 min-w-[120px]">
              <span className="text-sm text-muted-foreground">Progress:</span>
              <span className="text-sm font-medium">{statusConfig.progress}%</span>
            </div>
            <div className="flex items-center gap-2 min-w-[100px]">
              <Users className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-medium">{stats.seats} seats</span>
            </div>
            <div className="flex items-center gap-2 min-w-[100px]">
              <Phone className="w-4 h-4 text-brand-accent" />
              <span className="text-sm font-medium">{stats.dids} DIDs</span>
            </div>
            <div className="flex items-center gap-2 min-w-[120px]">
              <span className="text-sm text-muted-foreground">Updated:</span>
              <span className="text-sm font-medium">
                {new Date(project.updatedAt).toLocaleDateString()}
              </span>
            </div>
          </CardContent>
        )}
      </Card>
    </motion.div>
  );
}

/**
 * Dashboard page - Shows all projects for the current user
 */
export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [customerCompany, setCustomerCompany] = useState("");
  const [projectType, setProjectType] = useState<"new_customer" | "new_location" | "mac_order">("new_customer");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerEmails, setCustomerEmails] = useState<string[]>([]);

  // Project details state
  const [seats, setSeats] = useState<number | "">("");
  const [didsCount, setDidsCount] = useState<number | "">("");
  const [e911Count, setE911Count] = useState<number | "">("");
  const [webfaxCount, setWebfaxCount] = useState<number | "">("");
  const [callCenterAgents, setCallCenterAgents] = useState<number | "">("");
  const [callCenterSupervisors, setCallCenterSupervisors] = useState<number | "">("");
  const [smsUsers, setSmsUsers] = useState<number | "">("");
  const [hasCallRecording, setHasCallRecording] = useState(false);
  const [hasTranscriptionSentiment, setHasTranscriptionSentiment] = useState(false);
  const [callRecordingScope, setCallRecordingScope] = useState<"per_user" | "all_domains">("per_user");
  const [recordingUsers, setRecordingUsers] = useState<number | "">("");
  const [domainName, setDomainName] = useState("");
  const [reseller, setReseller] = useState("TeleCloud");
  const [didsType, setDidsType] = useState<"porting" | "new">("new");
  const [hasTollFree, setHasTollFree] = useState(false);
  const [tollFreeType, setTollFreeType] = useState<"porting" | "new">("new");
  const [portingDidNumbers, setPortingDidNumbers] = useState("");
  const [portingTfnNumbers, setPortingTfnNumbers] = useState("");

  // Wizard step visibility
  const [showCompanyInfo, setShowCompanyInfo] = useState(true);
  const [showExtensions, setShowExtensions] = useState(true);
  const [showPhoneNumbers, setShowPhoneNumbers] = useState(true);
  const [showMailboxes, setShowMailboxes] = useState(true);
  const [showAutoAttendants, setShowAutoAttendants] = useState(true);
  const [showTimeframes, setShowTimeframes] = useState(true);
  const [showHolidays, setShowHolidays] = useState(true);
  const [showTodRouters, setShowTodRouters] = useState(true);
  const [showSmsCompliance, setShowSmsCompliance] = useState(true);
  const [showInstall, setShowInstall] = useState(true);
  const [showNetwork, setShowNetwork] = useState(true);
  const [showSites, setShowSites] = useState(true);
  const [showPorting, setShowPorting] = useState(true);
  const [showGreetings, setShowGreetings] = useState(false);

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; projectId: string; projectName: string }>({
    isOpen: false,
    projectId: "",
    projectName: "",
  });
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // Filters and search
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"recent" | "name" | "status">("recent");

  const { data: projects, isLoading, refetch } = trpc.projects.list.useQuery();

  // Filter and sort projects
  const filteredAndSortedProjects = projects
    ? projects
        .filter((project) => {
          // Search filter
          const matchesSearch = project.name
            .toLowerCase()
            .includes(searchQuery.toLowerCase());

          // Status filter
          const matchesStatus =
            statusFilter === "all" || project.status === statusFilter;

          return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
          // Sorting
          switch (sortBy) {
            case "name":
              return a.name.localeCompare(b.name);
            case "status":
              return a.status.localeCompare(b.status);
            case "recent":
            default:
              return (
                new Date(b.updatedAt).getTime() -
                new Date(a.updatedAt).getTime()
              );
          }
        })
    : [];

  const createProject = trpc.projects.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Project "${data.project.name}" created successfully!`);
      setIsCreateDialogOpen(false);
      resetForm();
      refetch();
      // Navigate to the new project
      navigate(`/projects/${data.project.id}`);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create project");
    },
  });

  const deleteProject = trpc.projects.deleteProject.useMutation({
    onSuccess: () => {
      toast.success(`Project deleted successfully`);
      setDeleteDialog({ isOpen: false, projectId: "", projectName: "" });
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete project");
    },
  });

  const handleDeleteClick = (projectId: string, projectName: string) => {
    setDeleteDialog({ isOpen: true, projectId, projectName });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.projectId) {
      deleteProject.mutate({ projectId: deleteDialog.projectId });
    }
  };

  const resetForm = () => {
    setProjectName("");
    setCustomerCompany("");
    setCustomerEmail("");
    setCustomerEmails([]);
    setSeats("");
    setDidsCount("");
    setE911Count("");
    setWebfaxCount("");
    setCallCenterAgents("");
    setCallCenterSupervisors("");
    setSmsUsers("");
    setHasCallRecording(false);
    setHasTranscriptionSentiment(false);
    setCallRecordingScope("per_user");
    setRecordingUsers("");
    setDomainName("");
    setReseller("TeleCloud");
    setDidsType("new");
    setHasTollFree(false);
    setTollFreeType("new");
    setPortingDidNumbers("");
    setPortingTfnNumbers("");
    setShowCompanyInfo(true);
    setShowExtensions(true);
    setShowPhoneNumbers(true);
    setShowMailboxes(true);
    setShowAutoAttendants(true);
    setShowTimeframes(true);
    setShowHolidays(true);
    setShowTodRouters(true);
    setShowSmsCompliance(true);
    setShowInstall(true);
    setShowNetwork(true);
    setShowSites(true);
    setShowPorting(true);
    setShowGreetings(false);
    setProjectType("new_customer");
  };

  const handleAddEmail = () => {
    if (customerEmail && !customerEmails.includes(customerEmail)) {
      setCustomerEmails([...customerEmails, customerEmail]);
      setCustomerEmail("");
    }
  };

  const handleRemoveEmail = (email: string) => {
    setCustomerEmails(customerEmails.filter(e => e !== email));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Parse porting numbers if provided
    const didNumbers = portingDidNumbers
      ? portingDidNumbers.split(/[\n,]/).map(n => n.trim()).filter(n => n.length > 0)
      : undefined;
    const tfnNumbers = portingTfnNumbers
      ? portingTfnNumbers.split(/[\n,]/).map(n => n.trim()).filter(n => n.length > 0)
      : undefined;

    createProject.mutate({
      name: projectName,
      customerCompany,
      projectType,
      customerEmails,
      seats: seats !== "" ? seats : undefined,
      didsCount: didsCount !== "" ? didsCount : undefined,
      e911Count: e911Count !== "" ? e911Count : undefined,
      webfaxCount: webfaxCount !== "" ? webfaxCount : undefined,
      callCenterAgents: callCenterAgents !== "" ? callCenterAgents : undefined,
      callCenterSupervisors: callCenterSupervisors !== "" ? callCenterSupervisors : undefined,
      smsUsers: smsUsers !== "" ? smsUsers : undefined,
      hasCallRecording,
      hasTranscriptionSentiment: hasCallRecording && hasTranscriptionSentiment,
      callRecordingScope: hasCallRecording ? callRecordingScope : undefined,
      recordingUsers: hasCallRecording && callRecordingScope === "per_user" && recordingUsers !== "" ? recordingUsers : undefined,
      domainName: domainName || undefined,
      reseller: reseller || undefined,
      didsType,
      hasTollFree,
      tollFreeType: hasTollFree ? tollFreeType : undefined,
      portingDidNumbers: didsType === "porting" ? didNumbers : undefined,
      portingTfnNumbers: hasTollFree && tollFreeType === "porting" ? tfnNumbers : undefined,
      showCompanyInfo,
      showExtensions,
      showPhoneNumbers,
      showMailboxes,
      showAutoAttendants,
      showTimeframes,
      showHolidays,
      showTodRouters,
      showSmsCompliance,
      showInstall,
      showNetwork,
      showSites,
      showPorting,
      showGreetings,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Command Palette */}
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />

      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">TeleCloud Launchpad</h1>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCommandPaletteOpen(true)}
              className="hidden sm:flex"
            >
              <Command className="w-4 h-4 mr-2" />
              <span>Search</span>
              <kbd className="ml-2 hidden lg:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-xs">
                ⌘K
              </kbd>
            </Button>
            <span className="text-sm text-muted-foreground">
              Role: <strong>{user?.role}</strong>
            </span>
            {/* The old "Project Queue" + "Admin" buttons merged into a single
                "Open workspace" entry that drops employees into the shared
                workflow/admin shell (where the sidebar handles further
                navigation). Customers without an employee role see nothing. */}
            {(user?.isInternal || user?.role === "ADMIN" || user?.role === "PROVISIONER" || user?.role === "REVIEWER") && (
              <Button variant="outline" onClick={() => navigate("/workflow")}>
                <FileText className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/org-settings")}>
              Organization
            </Button>
            <ThemeToggle />
            <Button variant="outline" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">Projects</h2>
            <p className="text-muted-foreground">
              Manage your onboarding projects
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View toggle */}
            {projects && projects.length > 0 && (
              <div className="flex items-center gap-1 border rounded-lg p-1">
                <Button
                  variant={viewMode === "grid" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="gap-2"
                >
                  <Grid3x3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="gap-2"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            )}
            {(user?.isInternal || user?.role === "ADMIN" || user?.role === "PROVISIONER") && (
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 3,
                }}
              >
                <Button variant="brand" onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  New Project
                </Button>
              </motion.div>
            )}
          </div>
        </div>

        {/* Filters and Search */}
        {projects && projects.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="glass-card p-4 mb-6"
          >
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="provisioned">Provisioned</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as "recent" | "name" | "status")}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SortAsc className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active Filters Summary */}
            {(searchQuery || statusFilter !== "all") && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2 mt-3 pt-3 border-t border-border"
              >
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchQuery && (
                  <Badge variant="secondary" className="gap-1">
                    Search: {searchQuery}
                    <button
                      onClick={() => setSearchQuery("")}
                      className="hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {statusFilter !== "all" && (
                  <Badge variant="secondary" className="gap-1">
                    Status: {statusFilter.replace("_", " ")}
                    <button
                      onClick={() => setStatusFilter("all")}
                      className="hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery("");
                    setStatusFilter("all");
                  }}
                  className="ml-auto"
                >
                  Clear all
                </Button>
              </motion.div>
            )}
          </motion.div>
        )}

        {isLoading ? (
          <div className="py-12">
            <LoadingSpinner size="lg" text="Loading projects..." />
          </div>
        ) : projects && projects.length > 0 ? (
          filteredAndSortedProjects.length > 0 ? (
            <motion.div
              layout
              className={viewMode === "grid" ? "grid gap-4 md:grid-cols-2 lg:grid-cols-3" : "space-y-4"}
            >
              <AnimatePresence mode="popLayout">
                {filteredAndSortedProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    index={index}
                    viewMode={viewMode}
                    onNavigate={() => navigate(`/projects/${project.id}`)}
                    activeMenuId={activeMenuId}
                    setActiveMenuId={setActiveMenuId}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card variant="glass" className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Search className="w-16 h-16 text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No projects found</h3>
                  <p className="text-muted-foreground mb-6 text-center max-w-md">
                    No projects match your current filters. Try adjusting your search or filters.
                  </p>
                  <Button
                    variant="outline-brand"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("all");
                    }}
                  >
                    Clear filters
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                {/* Animated illustration */}
                <div className="relative mb-6">
                  <motion.div
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="relative"
                  >
                    <div className="w-24 h-24 bg-brand-primary/10 rounded-full flex items-center justify-center">
                      <Sparkles className="w-12 h-12 text-blue-600" />
                    </div>
                  </motion.div>

                  {/* Floating particles */}
                  <motion.div
                    animate={{
                      x: [0, 10, 0],
                      y: [0, -15, 0],
                      rotate: [0, 180, 360],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute -top-2 -right-2"
                  >
                    <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                  </motion.div>

                  <motion.div
                    animate={{
                      x: [0, -10, 0],
                      y: [0, 15, 0],
                      rotate: [0, -180, -360],
                    }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1,
                    }}
                    className="absolute -bottom-2 -left-2"
                  >
                    <div className="w-3 h-3 bg-pink-400 rounded-full" />
                  </motion.div>
                </div>

                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl font-bold mb-2"
                >
                  {user?.role === "CUSTOMER" ? "No Projects Yet" : "Ready to Launch?"}
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-muted-foreground mb-6 text-center max-w-md"
                >
                  {user?.role === "CUSTOMER"
                    ? "You don't have any projects yet. Your administrator will invite you to collaborate on projects."
                    : "Create your first project to start onboarding customers and provisioning their VoIP systems."}
                </motion.p>

                {(user?.isInternal || user?.role === "ADMIN" || user?.role === "PROVISIONER") && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Button
                      onClick={() => setIsCreateDialogOpen(true)}
                      size="lg"
                      className="gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      Create Your First Project
                    </Button>
                  </motion.div>
                )}

                {/* Helpful hint */}
                {(user?.isInternal || user?.role === "ADMIN" || user?.role === "PROVISIONER") && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg max-w-md"
                  >
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Tip:</strong> Each project represents a customer onboarding. You'll configure extensions, phone numbers, and features, then submit for review before provisioning.
                    </p>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>

      {/* Create Project Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Provide initial project details and invite customer contacts
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="projectName">Project Name</Label>
                <Input
                  id="projectName"
                  placeholder="Acme Corp Onboarding"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerCompany">Customer Company</Label>
                <Input
                  id="customerCompany"
                  placeholder="Acme Plumbing, Inc."
                  value={customerCompany}
                  onChange={(e) => setCustomerCompany(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  The customer's organization. New projects are owned by this org (derived from the customer email domain).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectType">Project Type</Label>
                <Select value={projectType} onValueChange={(value) => setProjectType(value as "new_customer" | "new_location" | "mac_order")}>
                  <SelectTrigger id="projectType">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_customer">New Customer</SelectItem>
                    <SelectItem value="new_location">New Location</SelectItem>
                    <SelectItem value="mac_order">MAC Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerEmail">Customer Email</Label>
                <div className="flex gap-2">
                  <Input
                    id="customerEmail"
                    type="email"
                    placeholder="john@acme.com"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddEmail();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddEmail}
                    disabled={!customerEmail}
                  >
                    Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Add one or more customer contacts who will receive the invitation
                </p>
              </div>

              {customerEmails.length > 0 && (
                <div className="space-y-2">
                  <Label>Customer Contacts ({customerEmails.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {customerEmails.map((email) => (
                      <Badge key={email} variant="secondary" className="gap-1">
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Project Details Section */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Project Details</h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-2">
                    <Label htmlFor="domainName">Domain Name</Label>
                    <Input
                      id="domainName"
                      placeholder="acmecorp"
                      value={domainName}
                      onChange={(e) => setDomainName(e.target.value)}
                    />
                  </div>

                  {/* Right Column */}
                  <div className="space-y-2">
                    <Label htmlFor="reseller">Reseller</Label>
                    <Input
                      id="reseller"
                      placeholder="TeleCloud"
                      value={reseller}
                      onChange={(e) => setReseller(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seats">Seats</Label>
                    <Input
                      id="seats"
                      type="number"
                      min="0"
                      placeholder="10"
                      value={seats}
                      onChange={(e) => setSeats(e.target.value === "" ? "" : parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="didsCount">Phone Numbers (DIDs & TFNs)</Label>
                    <Input
                      id="didsCount"
                      type="number"
                      min="0"
                      placeholder="5"
                      value={didsCount}
                      onChange={(e) => setDidsCount(e.target.value === "" ? "" : parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="e911Count">E911 Addresses</Label>
                    <Input
                      id="e911Count"
                      type="number"
                      min="0"
                      placeholder="2"
                      value={e911Count}
                      onChange={(e) => setE911Count(e.target.value === "" ? "" : parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="webfaxCount">Webfax Licenses</Label>
                    <Input
                      id="webfaxCount"
                      type="number"
                      min="0"
                      placeholder="3"
                      value={webfaxCount}
                      onChange={(e) => setWebfaxCount(e.target.value === "" ? "" : parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="smsUsers">SMS Users</Label>
                    <Input
                      id="smsUsers"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={smsUsers}
                      onChange={(e) => setSmsUsers(e.target.value === "" ? "" : parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="callCenterSupervisors">Call Center Supervisors</Label>
                    <Input
                      id="callCenterSupervisors"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={callCenterSupervisors}
                      onChange={(e) => setCallCenterSupervisors(e.target.value === "" ? "" : parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="callCenterAgents">Call Center Agents</Label>
                    <Input
                      id="callCenterAgents"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={callCenterAgents}
                      onChange={(e) => setCallCenterAgents(e.target.value === "" ? "" : parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              {/* Features Section */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Features</h3>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hasCallRecording"
                      checked={hasCallRecording}
                      onCheckedChange={(checked) => setHasCallRecording(checked as boolean)}
                    />
                    <Label htmlFor="hasCallRecording" className="font-normal cursor-pointer">
                      Call Recording
                    </Label>
                  </div>

                  {hasCallRecording && (
                    <>
                      <div className="ml-6 space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="hasTranscriptionSentiment"
                            checked={hasTranscriptionSentiment}
                            onCheckedChange={(checked) => setHasTranscriptionSentiment(checked as boolean)}
                          />
                          <Label htmlFor="hasTranscriptionSentiment" className="font-normal cursor-pointer">
                            Transcription & Sentiment Analysis
                          </Label>
                        </div>

                        <div className="space-y-2">
                          <Label>Call Recording Scope</Label>
                          <RadioGroup value={callRecordingScope} onValueChange={(value) => setCallRecordingScope(value as "per_user" | "all_domains")}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="per_user" id="per_user" />
                              <Label htmlFor="per_user" className="font-normal cursor-pointer">Per User</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="all_domains" id="all_domains" />
                              <Label htmlFor="all_domains" className="font-normal cursor-pointer">Entire Domain</Label>
                            </div>
                          </RadioGroup>
                        </div>

                        {callRecordingScope === "per_user" && (
                          <div className="space-y-2">
                            <Label htmlFor="recordingUsers">Recording Users</Label>
                            <Input
                              id="recordingUsers"
                              type="number"
                              min="0"
                              placeholder="0"
                              value={recordingUsers}
                              onChange={(e) => setRecordingUsers(e.target.value === "" ? "" : parseInt(e.target.value))}
                            />
                            <p className="text-xs text-muted-foreground">Number of users allowed to have call recording enabled</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* DID Numbers Section */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">DID Numbers</h3>

                <div className="space-y-2">
                  <Label>DID Type</Label>
                  <RadioGroup value={didsType} onValueChange={(value) => setDidsType(value as "porting" | "new")}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="new" id="dids_new" />
                      <Label htmlFor="dids_new" className="font-normal cursor-pointer">New Numbers</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="porting" id="dids_porting" />
                      <Label htmlFor="dids_porting" className="font-normal cursor-pointer">Porting Numbers</Label>
                    </div>
                  </RadioGroup>
                </div>

                {didsType === "porting" && (
                  <div className="space-y-2">
                    <Label htmlFor="portingDidNumbers">DID Numbers to Port (one per line or comma-separated)</Label>
                    <Textarea
                      id="portingDidNumbers"
                      placeholder="5551234567&#10;5559876543"
                      value={portingDidNumbers}
                      onChange={(e) => setPortingDidNumbers(e.target.value)}
                      rows={4}
                    />
                  </div>
                )}
              </div>

              {/* Toll Free Numbers Section */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Toll-Free Numbers</h3>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hasTollFree"
                    checked={hasTollFree}
                    onCheckedChange={(checked) => setHasTollFree(checked as boolean)}
                  />
                  <Label htmlFor="hasTollFree" className="font-normal cursor-pointer">
                    Customer has Toll-Free Numbers
                  </Label>
                </div>

                {hasTollFree && (
                  <div className="ml-6 space-y-4">
                    <div className="space-y-2">
                      <Label>Toll-Free Type</Label>
                      <RadioGroup value={tollFreeType} onValueChange={(value) => setTollFreeType(value as "porting" | "new")}>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="new" id="tfn_new" />
                          <Label htmlFor="tfn_new" className="font-normal cursor-pointer">New Numbers</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="porting" id="tfn_porting" />
                          <Label htmlFor="tfn_porting" className="font-normal cursor-pointer">Porting Numbers</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {tollFreeType === "porting" && (
                      <div className="space-y-2">
                        <Label htmlFor="portingTfnNumbers">Toll-Free Numbers to Port (one per line or comma-separated)</Label>
                        <Textarea
                          id="portingTfnNumbers"
                          placeholder="8005551234&#10;8009876543"
                          value={portingTfnNumbers}
                          onChange={(e) => setPortingTfnNumbers(e.target.value)}
                          rows={4}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Wizard Step Visibility Section */}
            <div className="border-t pt-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-1">Wizard Step Visibility</h3>
                <p className="text-xs text-muted-foreground">Select which steps will be visible to collaborators in the wizard</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showCompanyInfo"
                    checked={showCompanyInfo}
                    onChange={(e) => setShowCompanyInfo(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showCompanyInfo" className="font-normal cursor-pointer">Company Info</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showExtensions"
                    checked={showExtensions}
                    onChange={(e) => setShowExtensions(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showExtensions" className="font-normal cursor-pointer">Extensions</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showPhoneNumbers"
                    checked={showPhoneNumbers}
                    onChange={(e) => setShowPhoneNumbers(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showPhoneNumbers" className="font-normal cursor-pointer">Phone Numbers</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showMailboxes"
                    checked={showMailboxes}
                    onChange={(e) => setShowMailboxes(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showMailboxes" className="font-normal cursor-pointer">Mailboxes</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showAutoAttendants"
                    checked={showAutoAttendants}
                    onChange={(e) => setShowAutoAttendants(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showAutoAttendants" className="font-normal cursor-pointer">Auto Attendants</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showTimeframes"
                    checked={showTimeframes}
                    onChange={(e) => setShowTimeframes(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showTimeframes" className="font-normal cursor-pointer">Timeframes</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showHolidays"
                    checked={showHolidays}
                    onChange={(e) => setShowHolidays(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showHolidays" className="font-normal cursor-pointer">Holidays</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showTodRouters"
                    checked={showTodRouters}
                    onChange={(e) => setShowTodRouters(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showTodRouters" className="font-normal cursor-pointer">TOD Routers</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showSmsCompliance"
                    checked={showSmsCompliance}
                    onChange={(e) => setShowSmsCompliance(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showSmsCompliance" className="font-normal cursor-pointer">SMS Compliance</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showInstall"
                    checked={showInstall}
                    onChange={(e) => setShowInstall(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showInstall" className="font-normal cursor-pointer">Install &amp; Migration</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showNetwork"
                    checked={showNetwork}
                    onChange={(e) => setShowNetwork(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showNetwork" className="font-normal cursor-pointer">Network</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showSites"
                    checked={showSites}
                    onChange={(e) => setShowSites(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showSites" className="font-normal cursor-pointer">Sites</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showPorting"
                    checked={showPorting}
                    onChange={(e) => setShowPorting(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showPorting" className="font-normal cursor-pointer">Number Porting</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showGreetings"
                    checked={showGreetings}
                    onChange={(e) => setShowGreetings(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="showGreetings" className="font-normal cursor-pointer">Greetings</Label>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createProject.isPending || !projectName || !customerCompany || customerEmails.length === 0}
              >
                {createProject.isPending ? "Creating..." : "Create Project"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.isOpen} onOpenChange={(open) => !open && setDeleteDialog({ isOpen: false, projectId: "", projectName: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.projectName}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialog({ isOpen: false, projectId: "", projectName: "" })}
              disabled={deleteProject.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteProject.isPending}
            >
              {deleteProject.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
